/**
 * ArticleCard component for displaying an article in lists or grids
 * Unified component with all variants
 */

'use client';

import { ArrowRight, Calendar, ExternalLink, Tag, User } from 'lucide-react';
import { buttonVariants } from '../components/ui/button';
import { useArticlesComponents } from '../config';
import { formatArticleDateShort } from '../lib/date-helpers';
import { cn } from '../lib/utils';
import type { Article } from '../types';
import { highlightText } from '../utils';

export type ArticleCardLayout = 'grid' | 'list' | 'compact' | 'default' | 'featured';
export type ArticleViewMode = 'internal' | 'external';

export interface ArticleCardProps {
  article: Article;
  /** Base URL for article links (e.g., '/news/articles') */
  baseUrl?: string;
  /** Layout variant for the card */
  variant?: ArticleCardLayout;
  /** View mode: 'internal' for same-site links, 'external' for spipUrl links */
  viewMode?: ArticleViewMode;
  /** Whether the entire card should be clickable (default: true) */
  clickable?: boolean;
  /** Whether to show sticky indicator (default: true) */
  showStickyIndicator?: boolean;
  /** Whether to display author information (default: false) */
  displayAuthor?: boolean;
  /** Search term for highlighting text */
  searchTerm?: string;
  // Legacy props for compatibility
  showImage?: boolean;
  showDate?: boolean;
  showAuthor?: boolean;
  showExcerpt?: boolean;
}

export interface ArticleCardData {
  createdDate: Date | null;
  excerpt: string;
  imageUrl: string | null;
  imageAlt: string;
  imageWidth: number | null;
  imageHeight: number | null;
}

/**
 * Extract text excerpt from HTML content
 */
function getTextExcerpt(html: string | null | undefined, maxLength = 150): string {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '');
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

/**
 * Extract and process article data for card rendering
 */
function useArticleCardData(article: Article): ArticleCardData {
  const createdTimestamp = article.created?.timestamp;
  const createdDate = createdTimestamp ? new Date(createdTimestamp * 1000) : null;

  // Extract excerpt from header HTML if available, otherwise use body summary or subtitle
  const excerpt =
    getTextExcerpt(article.header?.processed) || article.body?.summary || article.subtitle || '';

  // Extract image data with proper union narrowing
  // Use featuredImage first, then image, then first image from gallery
  let baseImage = null;

  // Check featuredImage - verify mediaImage exists (don't rely on __typename)
  if (article.featuredImage?.mediaImage) {
    baseImage = article.featuredImage.mediaImage;
  } else if (article.image) {
    baseImage = article.image;
  } else if (article.gallery?.[0]?.mediaImage) {
    baseImage = article.gallery[0].mediaImage;
  }

  const imageUrl = baseImage?.url ?? null;
  const imageAlt = baseImage?.alt ?? article.title;
  const imageWidth = baseImage?.width ?? null;
  const imageHeight = baseImage?.height ?? null;

  return {
    createdDate,
    excerpt,
    imageUrl,
    imageAlt,
    imageWidth,
    imageHeight,
  };
}

/**
 * ArticleCard component displays an article in various layouts
 *
 * @example
 * ```tsx
 * <ArticleCard
 *   article={article}
 *   baseUrl="/news/articles"
 *   variant="default"
 *   clickable={true}
 * />
 * ```
 */
export function ArticleCard({
  article,
  baseUrl,
  variant = 'default',
  viewMode,
  clickable = false,
  showStickyIndicator = true,
  displayAuthor = false,
  showImage = true,
  showDate = true,
  showAuthor = true,
  showExcerpt = true,
  searchTerm,
}: ArticleCardProps) {
  const { LinkComponent, ImageComponent, baseUrl: configBaseUrl } = useArticlesComponents();
  const finalBaseUrl = baseUrl || configBaseUrl;

  const { createdDate, excerpt, imageUrl, imageAlt, imageWidth, imageHeight } =
    useArticleCardData(article);

  // Extract author name safely
  const authorName =
    article.author &&
    typeof article.author === 'object' &&
    article.author !== null &&
    'name' in article.author &&
    typeof article.author.name === 'string'
      ? article.author.name
      : null;

  // Public-audience articles must always open their source URL in a new tab
  const isPublicAudience =
    article.targetAudience?.some((audience) => String(audience).toLowerCase() === 'public') ??
    false;

  // Determine view mode automatically if not provided
  const effectiveViewMode =
    viewMode || (isPublicAudience || article.spipUrl ? 'external' : 'internal');
  const isExternal = effectiveViewMode === 'external';
  const readLabel = isExternal ? 'Read more' : 'Read';
  const readLabelSuffix = article.title ? ` about ${article.title}` : '';

  // Build the full path based on view mode
  let href: string;
  if (isExternal && article.spipUrl) {
    // For external articles, prefix spipUrl with NEXT_PUBLIC_DRUPAL_BASE_URL
    const drupalBaseUrl = process.env.NEXT_DRUPAL_BACKEND_ARTICLES_URL || 'https://uic.org/com/';
    href = `${drupalBaseUrl}${article.spipUrl}`;
  } else if (article.path?.startsWith('/articles/')) {
    href = article.path.replace('/articles/', `${finalBaseUrl}/`);
  } else {
    href = `${finalBaseUrl}/${article.id}`;
  }

  // External articles should not be fully clickable (force clickable=false)
  const effectiveClickable = isExternal ? false : clickable;

  const Link = LinkComponent;
  const Image = ImageComponent;

  // Helper for highlighting text
  const safeHighlightText = (text?: string) => {
    if (!text) return '';
    if (!searchTerm) return text;
    return highlightText(text, searchTerm);
  };

  // Image component (shared structure)
  const imageInner = showImage && imageUrl && (
    <div className="relative aspect-video overflow-hidden">
      <Image
        src={imageUrl}
        alt={imageAlt}
        width={imageWidth || 800}
        height={imageHeight || 450}
        className={cn(
          'h-full w-full object-cover transition-transform duration-300',
          effectiveClickable ? 'group-hover:scale-105' : 'group-hover/image:scale-105'
        )}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />

      {/* Audience badge overlay - style similaire à eventType */}
      {article.targetAudience && article.targetAudience.length > 0 && (
        <div className="absolute left-2 top-2 sm:left-4 sm:top-4">
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-medium shadow-md backdrop-blur-sm sm:px-2 sm:py-1 sm:text-xs"
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.2)',
              borderLeft: '3px solid hsl(var(--primary))',
            }}
          >
            {String(article.targetAudience[0]).toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );

  // Shared content component
  const sharedContent = (
    <div className="flex flex-1 flex-col p-4">
      {/* Sticky indicator */}
      {showStickyIndicator && article.sticky && (
        <span className="mb-2 w-fit rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
          Featured
        </span>
      )}

      {/* Title */}
      {effectiveClickable ? (
        <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-tight transition-colors group-hover:text-primary">
          {safeHighlightText(article.title)}
        </h3>
      ) : (
        <Link
          href={href}
          className="mb-2 line-clamp-2 text-lg font-semibold leading-tight transition-colors hover:text-primary"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {safeHighlightText(article.title)}
        </Link>
      )}

      {/* Subtitle */}
      {article.subtitle && (
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          {safeHighlightText(article.subtitle)}
        </p>
      )}

      {/* Excerpt */}
      {showExcerpt && excerpt && (
        <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">
          {safeHighlightText(excerpt)}
        </p>
      )}

      {/* Meta info */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Calendar className="h-3 w-3" />
        {showDate && createdDate && (
          <time dateTime={createdDate.toISOString()} suppressHydrationWarning>
            {formatArticleDateShort(createdDate)}
          </time>
        )}

        {displayAuthor && authorName && (
          <>
            <span>•</span>
            <User className="h-3 w-3" />
            <span>{safeHighlightText(authorName)}</span>
          </>
        )}
      </div>

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Tag className="h-3 w-3 text-muted-foreground" />
          {article.tags.slice(0, 3).map((tag, index) => {
            if (typeof tag !== 'object' || tag === null) {
              return null;
            }
            const tagId = 'id' in tag && typeof tag.id === 'string' ? tag.id : `tag-${index}`;
            const tagName = 'name' in tag && typeof tag.name === 'string' ? tag.name : String(tag);

            return (
              <span
                key={tagId}
                className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                {safeHighlightText(tagName)}
              </span>
            );
          })}
          {article.tags.length > 3 && (
            <span className="text-xs text-muted-foreground">+{article.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Read Button - seulement si non-cliquable */}
      {!effectiveClickable && (
        <div className="mt-auto border-t pt-4">
          <Link
            href={href}
            className={buttonVariants({
              variant: 'outline',
              size: 'sm',
              className: 'w-full border-none',
            })}
            aria-label={`Read more about ${article.title}`}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {readLabel}
            {readLabelSuffix && <span className="sr-only">{readLabelSuffix}</span>}
            {isExternal ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
          </Link>
        </div>
      )}
    </div>
  );

  // Render based on effectiveClickable prop
  if (effectiveClickable) {
    // Version entièrement cliquable (legacy) - not used for external articles
    return (
      <Link
        href={href}
        className={cn(
          'group block h-full overflow-hidden rounded bg-slate-500/5 transition-all hover:shadow-lg',
          variant === 'compact' && 'flex gap-4',
          variant === 'list' && 'flex gap-4',
          variant === 'featured' && 'ring-2 ring-primary/20'
        )}
      >
        {imageInner}
        {sharedContent}
      </Link>
    );
  }

  // Version non-cliquable avec liens séparés (défaut + external articles)
  return (
    <article
      className={cn(
        'flex h-full flex-col overflow-hidden rounded bg-slate-500/5 transition-all hover:shadow-lg',
        variant === 'compact' && 'flex-row gap-4',
        variant === 'list' && 'flex-row gap-4',
        variant === 'featured' && 'ring-2 ring-primary/20'
      )}
    >
      {/* Image cliquable */}
      {imageInner && (
        <Link 
          href={href} 
          className="group/image block"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {imageInner}
        </Link>
      )}
      {sharedContent}
    </article>
  );
}
